import React, { useState, useMemo } from 'react';
import { Drill, TagPreference } from '@/components/drill-library/types/drill-types';
import { Program, ProgramSlot } from './types/program-types';
import { Plus, Edit, Trash2, Copy, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import DrillCard from '@/components/drill-library/DrillCard';
import DrillLibraryPanel from './DrillLibraryPanel';
import DrillLibrarySubheader from './DrillLibrarySubheader';
import ProgramPlannerSubheader from './ProgramPlannerSubheader';
import ResponsiveSplitView from './ResponsiveSplitView';
import Modal from '@/components/shared/Modal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { getDrillDomain, getTagValues } from '@/utils/drill-taxonomy';
import { DEFAULT_SLOT_LABELS, EXTENDED_SLOT_IDS, DRILL_PLAN_TEMPLATE_MAP } from './lib/program-structure';

interface ProgramBuilderProps {
    program: Program;
    onUpdate: (updater: (draft: Program) => void) => void;
    drills: Drill[];
    onOpenDrillPicker: (dayIndex: number, slotId: string) => void;
    tagPreferences: { [key: string]: TagPreference };
    rightColumns?: number; // Number of columns allocated to right panel
}

const DrillInSlot: React.FC<{
    drill: Drill;
    slotData: ProgramSlot;
    onUpdate: (field: keyof Omit<ProgramSlot, 'drillId'>, value: string) => void;
    onRemove: () => void;
    isCompact: boolean;
    isVeryCompact: boolean;
    buttonSize: "sm" | "default" | "lg";
    inputSize: "sm" | "default" | "lg";
    textSize: string;
    gapSize: string;
    gridCols: string;
}> = ({ drill, slotData, onUpdate, onRemove, isCompact, isVeryCompact, buttonSize, inputSize, textSize, gapSize, gridCols }) => {
    const videoId = drill.videoUrl ? drill.videoUrl.split('v=')[1]?.split('&')[0] : null;
    const thumbnailUrl = videoId 
        ? `https://img.youtube.com/vi/${videoId}/mqdefault.jpg` 
        : 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTYwIiBoZWlnaHQ9IjkwIiB2aWV3Qm94PSIwIDAgMTYwIDkwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTYwIiBoZWlnaHQ9IjkwIiBmaWxsPSIjMzc0MTUxIi8+Cjx0ZXh0IHg9IjgwIiB5PSI0NSIgZm9udC1mYW1pbHk9IkFyaWFsLCBzYW5zLXNlcmlmIiBmb250LXNpemU9IjEyIiBmaWxsPSIjOUNBM0FGIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5ObyBQcmV2aWV3PC90ZXh0Pgo8L3N2Zz4=';

    return (
        <div className={`bg-fg/10 rounded-lg ${isVeryCompact ? 'p-1' : isCompact ? 'p-1' : 'p-3'}`}>
            <div className={`flex ${isVeryCompact ? 'flex-col gap-1' : 'flex gap-2 xl:gap-3'}`}>
                {/* Thumbnail */}
                <div className={`${isVeryCompact ? 'w-full h-16' : isCompact ? 'w-16 h-10' : 'w-20 h-12'} flex-shrink-0`}>
                    <img
                        src={thumbnailUrl}
                        alt={drill.name}
                        className="w-full h-full object-cover rounded"
                    />
                </div>
                
                {/* Drill Info */}
                <div className={`flex-1 min-w-0 ${isVeryCompact ? 'space-y-1' : 'space-y-2'}`}>
                    <h4 className={`font-medium ${isVeryCompact ? 'text-sm' : 'text-base'} line-clamp-2`}>
                        {drill.name}
                    </h4>
                    <p className={`text-muted ${isVeryCompact ? 'text-xs' : 'text-sm'} truncate`}>
                        {getDrillDomain(drill)}
                    </p>
                    
                    {/* Tags */}
                    <div className={`flex gap-[3px] xl:gap-1 flex-wrap ${isVeryCompact ? 'max-h-4 overflow-hidden' : ''}`}>
                        {getTagValues(drill, 'Domain')
                            .slice(0, isVeryCompact ? 1 : 2)
                            .map((tag, tagIndex) => (
                                <Badge key={`${tag}-${tagIndex}`} variant="secondary" className="text-xs px-2 py-0">
                                    {tag}
                                </Badge>
                            ))}
                    </div>
                    
                    {/* Exercise Parameters */}
                    <div className={`grid ${gridCols} ${isVeryCompact ? 'gap-1 mb-1' : 'gap-[3px] xl:gap-2 mb-2'}`}>
                        <Input
                            type="text"
                            placeholder="Sets"
                            value={slotData.sets}
                            onChange={(e) => onUpdate('sets', e.target.value)}
                            className="h-9 text-sm"
                        />
                        <Input
                            type="text"
                            placeholder="Reps"
                            value={slotData.reps}
                            onChange={(e) => onUpdate('reps', e.target.value)}
                            className="h-9 text-sm"
                        />
                    </div>
                    
                    {/* Notes */}
                    <Textarea
                        placeholder="Notes..."
                        value={slotData.notes}
                        onChange={(e) => onUpdate('notes', e.target.value)}
                        className="text-sm min-h-20 resize-none"
                    />
                    
                    {/* Remove Button */}
                    {onRemove && (
                        <Button
                            onClick={onRemove}
                            variant="ghost"
                            size="sm"
                            className="text-status-deviation hover:text-status-deviation text-sm px-2 py-1 mt-1"
                        >
                            Remove
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

const ProgramBuilder: React.FC<ProgramBuilderProps> = ({ 
    program, 
    onUpdate, 
    drills, 
    onOpenDrillPicker, 
    tagPreferences,
    rightColumns = 3
}) => {
    const [currentDayIndex, setCurrentDayIndex] = useState(0);
    const [draggedDrillId, setDraggedDrillId] = useState<string | null>(null);
    const [editingSlotLabel, setEditingSlotLabel] = useState<string | null>(null);
    const [showSlotInfo, setShowSlotInfo] = useState<string | null>(null);
    const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
    const [filteredDrills, setFilteredDrills] = useState<Drill[]>(drills);
    const [selectedTemplate, setSelectedTemplate] = useState<'blank' | 'conor' | 'custom1'>('conor');

    // Update filtered drills when drills change
    React.useEffect(() => {
        setFilteredDrills(drills);
    }, [drills]);

    const currentDay = program.days[currentDayIndex];
    const slotOrder = EXTENDED_SLOT_IDS;

    // Responsive styling based on column width
    const isCompact = rightColumns <= 2;
    const isVeryCompact = rightColumns === 1;
    
    // Responsive classes
    const cardPadding = isVeryCompact ? 'p-2' : isCompact ? 'p-3' : 'p-4';
    const titleSize = 'text-base'; // Consistent with SOP Assessment labels
    const buttonSize = isVeryCompact ? 'sm' : isCompact ? 'sm' : 'default';
    const inputSize = 'default'; // Consistent sizing
    const textSize = 'text-sm'; // Consistent with SOP Assessment
    const gapSize = isVeryCompact ? 'gap-1' : isCompact ? 'gap-2' : 'gap-3';
    const gridCols = isVeryCompact ? 'grid-cols-1' : isCompact ? 'grid-cols-2' : 'grid-cols-2';

    const handleDrillSelect = (drillId: string) => {
        // Default to first slot when selecting from library; user can choose later
        const firstSlotId = Object.keys(DEFAULT_SLOT_LABELS)[0] || 'A1';
        onOpenDrillPicker(currentDayIndex, firstSlotId);
    };

    const handleDrillDrag = (drillId: string) => {
        setDraggedDrillId(drillId);
    };

    const handleDrillDragEnd = () => {
        setDraggedDrillId(null);
    };

    const handleFilterChange = ({ searchTerm, selectedPresets }: { searchTerm: string; selectedPresets: string[] }) => {
        let filtered = drills;

        // Apply search term filter
        if (searchTerm) {
            const q = searchTerm.toLowerCase();
            filtered = filtered.filter(drill => 
                drill.name.toLowerCase().includes(q) ||
                getDrillDomain(drill).toLowerCase().includes(q) ||
                getTagValues(drill, 'Domain').some(tag => tag.toLowerCase().includes(q))
            );
        }

        // Parse dropdown-provided filters: entries shaped as "Key:Value"
        const kvFilters: Array<[string, string]> = selectedPresets
            .map(p => (p.includes(':') ? (p.split(':').slice(0, 2) as [string, string]) : null))
            .filter(Boolean) as Array<[string, string]>;

        if (kvFilters.length > 0) {
            filtered = filtered.filter(drill => {
                return kvFilters.every(([key, value]) => {
                    const tagValues = getTagValues(drill, key);
                    return tagValues.includes(value);
                });
            });
        } else if (selectedPresets.length > 0) {
            // Back-compat with old preset IDs (weightroom/positional/etc.) targeting Domain
            filtered = filtered.filter(drill => {
                return selectedPresets.some(preset => {
                    const presetTags = getPresetTags(preset);
                    const domainTags = getTagValues(drill, 'Domain');
                    return presetTags.some(tag => domainTags.includes(tag));
                });
            });
        }

        setFilteredDrills(filtered);
    };

    const getPresetTags = (presetId: string): string[] => {
        const presetMap: { [key: string]: string[] } = {
            'weightroom': ['Weightroom'],
            'positional': ['Positional'],
            'apt': ['Anterior Orientation'],
            'swayback': ['Swayback'],
            'lateralisation': ['Left Lateralized', 'Right Lateralized'],
            'mobility': ['Mobility'],
            'stability': ['Stability'],
            'strength': ['Strength']
        };
        return presetMap[presetId] || [];
    };


    const handleDrop = (slotId: string) => {
        if (!draggedDrillId) return;
        
        onUpdate(draft => {
            if (!draft.days[currentDayIndex]) return;
            
            const newSlots: any = { ...(draft.days[currentDayIndex].slots || {}) };
            // Remove drill from any existing slot
            Object.keys(newSlots).forEach(key => {
                const s = newSlots[key];
                if (s && s.drillId === draggedDrillId) {
                    delete newSlots[key];
                }
            });
            
            // Add drill to new slot
            newSlots[slotId] = {
                drillId: draggedDrillId,
                sets: '',
                reps: '',
                rest: '',
                tempo: '',
                notes: ''
            };
            
            draft.days[currentDayIndex].slots = newSlots;
        });
        
        setDraggedDrillId(null);
    };

    const updateSlotData = (slotId: string, field: keyof Omit<ProgramSlot, 'drillId'>, value: string) => {
        onUpdate(draft => {
            if (!draft.days[currentDayIndex]?.slots?.[slotId]) return;
            
            draft.days[currentDayIndex].slots[slotId] = {
                ...draft.days[currentDayIndex].slots[slotId],
                [field]: value
            };
        });
    };

    const removeDrillFromSlot = (slotId: string) => {
        onUpdate(draft => {
            if (!draft.days[currentDayIndex]?.slots?.[slotId]) return;
            
            delete draft.days[currentDayIndex].slots[slotId];
        });
    };

    const buildTemplateSlots = (tpl: 'blank' | 'conor' | 'custom1') => {
        const makeEmpty = (): any => ({ drillId: '', sets: '', reps: '', rest: '', tempo: '', notes: '' });
        if (tpl === 'blank') {
            return { slots: {}, labels: {} } as any;
        }
        // Conor Harris Methodology full set
        const labelsList = [
            'Warm-Up 1: Corrective',
            'Warm-Up 2: Corrective',
            'Warm-Up 3: Corrective',
            'Warm-Up 4: Corrective',
            'Lower Push',
            'Rest',
            'Lower Push',
            'Upper Pull',
            'Accessory',
            'Core',
            'Cooldown'
        ];
        const slots: any = {};
        const labels: any = {};
        for (let i = 0; i < labelsList.length && i < slotOrder.length; i++) {
            slots[slotOrder[i]] = makeEmpty();
            labels[slotOrder[i]] = labelsList[i];
        }
        return { slots, labels } as any;
    };

    const applyTemplateToDay = (dayIndex: number, tpl: 'blank' | 'conor' | 'custom1') => {
        onUpdate(draft => {
            const { slots, labels } = buildTemplateSlots(tpl);
            draft.days[dayIndex].slots = slots;
            draft.slotLabels = { ...(draft.slotLabels as any), ...labels } as any;
        });
    };

    const addDay = () => {
        onUpdate(draft => {
            const dayNumber = draft.days.length + 1;
            const { slots, labels } = buildTemplateSlots(selectedTemplate);
            draft.days.push({ name: `Day ${dayNumber}`, notes: '', slots } as any);
            draft.slotLabels = { ...(draft.slotLabels as any), ...labels } as any;
        });
    };

    // Template selection is now only for new days; do NOT auto-apply to existing day
    // (removed auto-apply effect to prevent overwriting user's work)

    // Ensure initial day is populated if empty on first load
    React.useEffect(() => {
        const day = program.days[currentDayIndex];
        if (!day) return;
        const hasSlots = day.slots && Object.keys(day.slots).length > 0;
        if (!hasSlots) {
            applyTemplateToDay(currentDayIndex, selectedTemplate);
        }
    }, [currentDayIndex]);

    const duplicateDay = (dayIndex: number) => {
        onUpdate(draft => {
            const dayToDuplicate = draft.days[dayIndex];
            const newDay = {
                ...dayToDuplicate,
                name: `${dayToDuplicate.name} (Copy)`,
                notes: dayToDuplicate.notes || '',
                slots: { ...dayToDuplicate.slots }
            };
            draft.days.splice(dayIndex + 1, 0, newDay);
        });
    };

    const clearDay = (dayIndex: number) => {
        onUpdate(draft => {
            if (draft.days[dayIndex]) {
                draft.days[dayIndex].slots = {};
            }
        });
    };

    const deleteDay = (dayIndex: number) => {
        if (program.days.length <= 1) return; // Keep at least one day
        
        // Calculate new current day index before updating
        const newCurrentDayIndex = currentDayIndex >= dayIndex ? Math.max(0, currentDayIndex - 1) : currentDayIndex;
        
        onUpdate(draft => {
            draft.days.splice(dayIndex, 1);
        });
        
        // Update current day index after the program update
        if (newCurrentDayIndex !== currentDayIndex) {
            setCurrentDayIndex(newCurrentDayIndex);
        }
    };

    const updateDayName = (dayIndex: number, newName: string) => {
        onUpdate(draft => {
            if (draft.days[dayIndex]) {
                draft.days[dayIndex].name = newName;
            }
        });
    };

    const applyTemplate = (templateType: string) => {
        const template = DRILL_PLAN_TEMPLATE_MAP[templateType];
        const templateDays = template?.dayNames ?? ['Day 1'];

        onUpdate(draft => {
            draft.days = templateDays.map((name) => ({
                name,
                notes: '',
                slots: {}
            }));
        });

        setCurrentDayIndex(0);
        setIsTemplateModalOpen(false);
    };

    const getSlotPurpose = (slotId: string) => {
        const purposes: { [key: string]: string } = {
            'A1': 'Primary movement for lower body pushing',
            'A2': 'Primary movement for upper body pulling',
            'B1': 'Secondary movement for upper body pushing',
            'B2': 'Secondary movement for lower body pulling',
            'C1': 'Core stability and anti-movement',
            'C2': 'Isolation or Accessory movement'
        };
        return purposes[slotId] || 'General exercise slot';
    };

    const getMovementPattern = (slotId: string) => {
        const patterns: { [key: string]: string } = {
            'A1': 'Squat, Lunge, or Hip Hinge',
            'A2': 'Pull-up, Row, or Pull variation',
            'B1': 'Push-up, Press, or Push variation',
            'B2': 'Deadlift, RDL, or Pull variation',
            'C1': 'Plank, Anti-rotation, or Stability',
            'C2': 'Isolation or Accessory movement'
        };
        return patterns[slotId] || 'Various movement patterns';
    };

    const slotTemplate = slotOrder;

    // Wrapper component to filter out rightColumns prop
    const RightPanelWrapper = ({ rightColumns: _, ...props }: any) => (
        <Card className="h-full flex flex-col" {...props}>
            {props.children}
        </Card>
    );

    return (
        <>
        <div className="h-full flex flex-col">
            {/* Subheaders */}
            <div className="flex border-b border-line">
                {/* Left Subheader - Drill Library Controls (50%) */}
                <div className="w-1/2">
                    <DrillLibrarySubheader
                        drills={drills}
                        tagPreferences={tagPreferences}
                        onFilterChange={handleFilterChange}
                        leftColumns={3}
                    />
                </div>
                
                {/* Right Subheader - Program Planner Controls (50%) */}
                <div className="w-1/2">
                    <ProgramPlannerSubheader
                        program={program}
                        currentDayIndex={currentDayIndex}
                        onUpdate={onUpdate}
                        onAddDay={addDay}
                        onDuplicateDay={duplicateDay}
                        onClearDay={clearDay}
                        onDeleteDay={deleteDay}
                        onSetCurrentDay={setCurrentDayIndex}
                        rightColumns={3}
                        selectedTemplate={selectedTemplate}
                        onSelectTemplate={(tpl) => setSelectedTemplate(tpl)}
                    />
                </div>
            </div>

            {/* Main Viewport */}
            <div className="flex-1 min-h-0">
                <ResponsiveSplitView
                    leftPanel={
                        <DrillLibraryPanel
                            drills={filteredDrills}
                            tagPreferences={tagPreferences}
                            onDrillSelect={(id) => {
                                // Selecting from the library sets the candidate; user can choose later
                                // Keep for potential keyboard flows
                                setDraggedDrillId(id);
                            }}
                            onDrillDrag={handleDrillDrag}
                            onDrillDragEnd={handleDrillDragEnd}
                            selectedDrillId={draggedDrillId}
                            onSelectDrillCandidate={(id) => setDraggedDrillId(id)}
                            onTagClick={(tag) => {
                                // Quick filter by clicked tag
                                setFilteredDrills(drills.filter(d => {
                                    const vals = Object.values(d.tags || {});
                                    const flat = vals.flatMap(v => Array.isArray(v) ? v : [v]);
                                    return flat.includes(tag);
                                }));
                            }}
                            onAddDrill={(id) => {
                                // Add to first available slot if none selected
                                const defaultOrder = ['A1','A2','B1','B2','C1','C2'];
                                const day = program.days[currentDayIndex];
                                const existing = day.slots || {} as any;
                                const first = defaultOrder.find(k => !existing[k]) || defaultOrder[0];
                                onUpdate(draft => {
                                    if (!draft.days[currentDayIndex].slots) draft.days[currentDayIndex].slots = {} as any;
                                    draft.days[currentDayIndex].slots[first] = { drillId: id, sets:'', reps:'', rest:'', tempo:'', notes:'' } as any;
                                });
                                setDraggedDrillId(null);
                            }}
                            nextSlotLabel={(() => {
                                const defaultOrder = ['A1','A2','B1','B2','C1','C2'];
                                const day = program.days[currentDayIndex];
                                const existing = day.slots || {} as any;
                                const first = defaultOrder.find(k => !existing[k]) || defaultOrder[0];
                                const label = (program.slotLabels && (program.slotLabels as any)[first]) || first;
                                return label;
                            })()}
                        />
                    }
                    rightPanel={
                <RightPanelWrapper>
                <CardContent className="flex-1 overflow-y-auto p-1 xl:p-4">
                    {/* Program Slots */}
                    <div className={`space-y-${isVeryCompact ? '2' : isCompact ? '3' : '4'}`}> 
                        {slotTemplate.filter(id => (currentDay?.slots && currentDay.slots[id] !== undefined)).map((slotId, idx) => {
                            const customLabel = program.slotLabels?.[slotId];
                                const label = customLabel || DEFAULT_SLOT_LABELS[slotId];
                            const slotData = currentDay?.slots?.[slotId];
                            const drill = slotData ? drills.find(d => d.id === slotData.drillId) : null;

                            return (
                                <Card
                                    key={slotId}
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={() => handleDrop(slotId)}
                                    className="border-2 border-dashed hover:border-accent transition-colors"
                                >
                                    <CardHeader className={isVeryCompact ? 'p-1' : isCompact ? 'p-2' : 'p-4'}>
                                        <div className={`flex items-center ${isVeryCompact ? 'flex-col gap-1' : 'justify-between'}`}>
                                            <div className={`flex items-center ${isVeryCompact ? 'gap-1' : 'gap-2'}`}>
                                                {editingSlotLabel === slotId ? (
                                                    <Input
                                                        type="text"
                                                        value={label}
                                                        onChange={(e) => onUpdate(draft => {
                                                            if (!draft.slotLabels) draft.slotLabels = {};
                                                            draft.slotLabels[slotId] = e.target.value;
                                                        })}
                                                        onBlur={() => setEditingSlotLabel(null)}
                                                        onKeyDown={(e) => e.key === 'Enter' && setEditingSlotLabel(null)}
                                                        className="text-base font-medium"
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <h3 
                                                        className="text-base font-medium cursor-pointer hover:text-accent"
                                                        onClick={() => setEditingSlotLabel(slotId)}
                                                    >
                                                        {label}
                                                    </h3>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => setShowSlotInfo(showSlotInfo === slotId ? null : slotId)}
                                                    className={isVeryCompact ? 'h-4 w-4 p-0' : isCompact ? 'h-5 w-5 p-0' : 'h-6 w-6 p-0'}
                                                >
                                                    <Info className={isVeryCompact ? 'h-2 w-2' : isCompact ? 'h-3 w-3' : 'h-4 w-4'} />
                                                </Button>
                                            </div>
                                            {!isVeryCompact && (
                                                <>
                                                <Button
                                                    onClick={() => {
                                                        if (draggedDrillId) {
                                                            // If a drill is selected, place it directly into this slot
                                                            onUpdate(draft => {
                                                                if (!draft.days[currentDayIndex]) return;
                                                                if (!draft.days[currentDayIndex].slots) draft.days[currentDayIndex].slots = {} as any;
                                                                draft.days[currentDayIndex].slots[slotId] = {
                                                                    drillId: draggedDrillId,
                                                                    sets: '', reps: '', rest: '', tempo: '', notes: ''
                                                                } as any;
                                                            });
                                                            setDraggedDrillId(null);
                                                        } else {
                                                            onOpenDrillPicker(currentDayIndex, slotId);
                                                        }
                                                    }}
                                                    size={buttonSize}
                                                    variant="outline"
                                                    className={isCompact ? 'h-6 w-6 p-0' : ''}
                                                >
                                                    <Plus className={isCompact ? 'h-3 w-3' : 'h-4 w-4'} />
                                                </Button>
                                                
                                                <Button
                                                    onClick={() => {
                                                        // Delete this slot and shift lower slots upward
                                                        onUpdate(draft => {
                                                            const order = ['A1','A2','B1','B2','C1','C2'];
                                                            const day = draft.days[currentDayIndex];
                                                            const start = order.indexOf(slotId);
                                                            for (let i = start; i < order.length - 1; i++) {
                                                                (day.slots as any)[order[i]] = (day.slots as any)[order[i+1]];
                                                            }
                                                            delete (day.slots as any)[order[order.length - 1]];
                                                        });
                                                    }}
                                                    size={buttonSize}
                                                    variant="ghost"
                                                    className={isCompact ? 'h-6 w-6 p-0' : ''}
                                                    title="Delete this slot"
                                                >
                                                    <Trash2 className={isCompact ? 'h-3 w-3' : 'h-4 w-4'} />
                                                </Button>
                                                </>
                                            )}
                                        </div>
                                        
                                        {showSlotInfo === slotId && (
                                            <div className="mt-2 p-2 bg-fg/10 rounded text-sm">
                                                <p><strong>Purpose:</strong> {getSlotPurpose(slotId)}</p>
                                                <p><strong>Movement Pattern:</strong> {getMovementPattern(slotId)}</p>
                                            </div>
                                        )}
                                    </CardHeader>

                                    <CardContent className={isVeryCompact ? 'p-1' : 'p-1 xl:p-4'}>
                                        {/* Slot Card - editable controls, with or without drill */}
                                        <div className="space-y-2">
                                            {drill ? (
                                                <DrillInSlot
                                                    drill={drill}
                                                    slotData={slotData}
                                                    onUpdate={(field, value) => updateSlotData(slotId, field, value)}
                                                    onRemove={() => removeDrillFromSlot(slotId)}
                                                    isCompact={isCompact}
                                                    isVeryCompact={isVeryCompact}
                                                    buttonSize={buttonSize}
                                                    inputSize={inputSize}
                                                    textSize={textSize}
                                                    gapSize={gapSize}
                                                    gridCols={gridCols}
                                                />
                                            ) : (
                                                <div className="text-center py-8 text-muted text-sm">
                                                    <p>Drag a drill here or click "+" to add selected drill</p>
                                                </div>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                        {/* Bottom add-slot control */}
                        <div className="flex justify-center py-2">
                            <Button size={buttonSize} onClick={() => {
                                onUpdate(draft => {
                                    const order = ['A1','A2','B1','B2','C1','C2'];
                                    const day = draft.days[currentDayIndex];
                                    for (let i = 0; i < order.length; i++) {
                                        const k = order[i];
                                        if ((day.slots as any)[k] === undefined) {
                                            (day.slots as any)[k] = { drillId: '', sets: '', reps: '', rest: '', tempo: '', notes: '' } as any;
                                            if (!draft.slotLabels) draft.slotLabels = {} as any;
                                            (draft.slotLabels as any)[k] = (DEFAULT_SLOT_LABELS as any)[k] || k;
                                            break;
                                        }
                                    }
                                });
                            }} title="Add slot at end">
                                <Plus className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
                </RightPanelWrapper>
            }
                />
            </div>
        </div>

        {/* Template Modal */}
        <Modal
            isOpen={isTemplateModalOpen}
            onClose={() => setIsTemplateModalOpen(false)}
            title="Program Templates"
            helpKey="program-templates"
        >
            <div className="p-6">
                <p className="text-muted mb-6">
                    Choose a template to quickly set up your program structure. This will replace your current days.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card 
                        onClick={() => applyTemplate('push_pull_legs')}
                        className="cursor-pointer hover:border-accent transition-colors"
                    >
                        <CardHeader>
                            <CardTitle className="text-lg">Push/Pull/Legs</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-sm text-muted mb-3">
                                3-day split focusing on movement patterns
                            </p>
                            <ul className="text-xs text-muted space-y-1">
                                <li>• Push Day</li>
                                <li>• Pull Day</li>
                                <li>• Legs Day</li>
                            </ul>
                        </CardContent>
                    </Card>

                    <Card 
                        onClick={() => applyTemplate('upper_lower')}
                        className="cursor-pointer hover:border-accent transition-colors"
                    >
                        <CardHeader>
                            <CardTitle className="text-lg">Upper/Lower</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-sm text-muted mb-3">
                                2-day split for balanced training
                            </p>
                            <ul className="text-xs text-muted space-y-1">
                                <li>• Upper Body</li>
                                <li>• Lower Body</li>
                            </ul>
                        </CardContent>
                    </Card>

                    <Card 
                        onClick={() => applyTemplate('full_body')}
                        className="cursor-pointer hover:border-accent transition-colors"
                    >
                        <CardHeader>
                            <CardTitle className="text-lg">Full Body</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <p className="text-sm text-muted mb-3">
                                Single day template for complete training
                            </p>
                            <ul className="text-xs text-muted space-y-1">
                                <li>• Full Body</li>
                            </ul>
                        </CardContent>
                    </Card>
                </div>

                <div className="flex justify-end mt-6">
                    <Button
                        onClick={() => setIsTemplateModalOpen(false)}
                        variant="outline"
                    >
                        Cancel
                    </Button>
                </div>
            </div>
        </Modal>
        </>
    );
};

export default ProgramBuilder;
